import React, { useEffect, useMemo, useState } from "react";
import { Plus, Loader2, MapPin, ChevronLeft, ChevronRight, Camera, X, Trash2, Pencil, Paperclip, CalendarDays } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { clientLabel } from "../lib/api";
import { SearchableSelect } from "../components/SearchableSelect";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";

const STATUS_COLORS = {
  Programada: "bg-blue-100 text-blue-700", Realizada: "bg-green-100 text-green-700",
  Reprogramada: "bg-amber-100 text-amber-700", Cancelada: "bg-red-100 text-red-700",
};
const DOW = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const fileUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${id}?auth=${localStorage.getItem("selec_token")}`;
const pad = (n) => String(n).padStart(2, "0");
const emptyVisit = { party_type: "client", client_id: "", prospect_id: "", contact_name: "", date: "", time: "", purpose: "", location: "", status: "Programada", result: "", notes: "", evidence: [] };

export default function Visits() {
  const { can } = useAuth();
  const [cursor, setCursor] = useState(new Date());
  const [visits, setVisits] = useState([]);
  const [clients, setClients] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyVisit);
  const [uploading, setUploading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [delTarget, setDelTarget] = useState(null);

  const monthStr = `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}`;

  const load = () => {
    setLoading(true);
    Promise.all([api.get(`/visits?month=${monthStr}`), api.get("/clients"), api.get("/prospects")])
      .then(([v, c, p]) => { setVisits(v.data); setClients(c.data); setProspects(p.data); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [monthStr]);

  const byDay = useMemo(() => {
    const m = {};
    visits.forEach((v) => { (m[v.date] = m[v.date] || []).push(v); });
    return m;
  }, [visits]);

  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = new Date(first);
    start.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);

  const openCreate = (dateStr) => { setEditing(null); setForm({ ...emptyVisit, date: dateStr || "" }); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({ party_type: v.prospect_id ? (prospects.find((p) => p.id === v.prospect_id)?.segment === "cold" ? "cold" : "prospect") : "client", client_id: v.client_id || "", prospect_id: v.prospect_id || "", contact_name: v.contact_name || "", date: v.date || "", time: v.time || "", purpose: v.purpose || "", location: v.location || "", status: v.status, result: v.result || "", notes: v.notes || "", evidence: v.evidence || [] });
    setSelectedDay(null); setOpen(true);
  };

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setForm((x) => ({ ...x, evidence: [...x.evidence, data] })); }
    catch (er) { toast.error(formatApiError(er.response?.data?.detail)); } finally { setUploading(false); e.target.value = ""; }
  };
  const rmEvidence = (id) => setForm((x) => ({ ...x, evidence: x.evidence.filter((f) => f.id !== id) }));

  const save = async () => {
    if (form.party_type === "client" && !form.client_id) return toast.error("Selecciona un cliente");
    if ((form.party_type === "prospect" || form.party_type === "cold") && !form.prospect_id) return toast.error("Selecciona un prospecto o cliente frío");
    if (!form.date) return toast.error("Selecciona la fecha");
    const payload = { ...form };
    if (form.party_type === "client") payload.prospect_id = ""; else payload.client_id = "";
    try {
      if (editing) await api.put(`/visits/${editing.id}`, payload);
      else await api.post("/visits", payload);
      toast.success("Visita guardada"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => { try { await api.delete(`/visits/${delTarget.id}`); toast.success("Visita eliminada"); setDelTarget(null); setSelectedDay(null); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const dayVisits = selectedDay ? (byDay[selectedDay] || []) : [];

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} data-testid="visit-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
          <h2 className="text-lg font-heading font-bold w-48 text-center" data-testid="visit-month-label">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</h2>
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} data-testid="visit-next-month"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" className="rounded-sm text-xs" onClick={() => setCursor(new Date())}>Hoy</Button>
        </div>
        {can("visits:create") && <Button onClick={() => openCreate("")} className="rounded-sm gap-2 font-bold uppercase tracking-wider text-xs" data-testid="new-visit-btn"><Plus className="h-4 w-4" /> Programar visita</Button>}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
        <Card className="rounded-sm border shadow-none overflow-hidden">
          <div className="grid grid-cols-7 bg-muted text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            {DOW.map((d) => <div key={d} className="px-2 py-2 text-center">{d}</div>)}
          </div>
          <div className="grid grid-cols-7" data-testid="visit-calendar">
            {grid.map((d, i) => {
              const ds = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
              const inMonth = d.getMonth() === cursor.getMonth();
              const dayItems = byDay[ds] || [];
              const isToday = ds === `${new Date().getFullYear()}-${pad(new Date().getMonth() + 1)}-${pad(new Date().getDate())}`;
              return (
                <div key={i} className={`min-h-[92px] border-t border-l border-border p-1.5 ${i % 7 === 6 ? "border-r" : ""} ${!inMonth ? "bg-muted/30" : "cursor-pointer hover:bg-muted/40"}`}
                     onClick={() => inMonth && (dayItems.length ? setSelectedDay(ds) : can("visits:create") && openCreate(ds))} data-testid={`visit-day-${ds}`}>
                  <div className={`text-xs font-semibold ${isToday ? "bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center" : inMonth ? "" : "text-muted-foreground"}`}>{d.getDate()}</div>
                  <div className="mt-1 space-y-0.5">
                    {dayItems.slice(0, 3).map((v) => (
                      <div key={v.id} className={`text-[10px] px-1 py-0.5 rounded-sm truncate ${STATUS_COLORS[v.status] || "bg-muted"}`} title={v.party_name}>{v.time ? `${v.time} ` : ""}{v.party_name}</div>
                    ))}
                    {dayItems.length > 3 && <div className="text-[10px] text-muted-foreground px-1">+{dayItems.length - 3} más</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Day detail dialog */}
      <Dialog open={!!selectedDay} onOpenChange={(o) => !o && setSelectedDay(null)}>
        <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="visit-day-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><CalendarDays className="h-5 w-5 text-primary" /> Visitas del {selectedDay}</DialogTitle>
            <DialogDescription>{dayVisits.length} visita(s) programada(s).</DialogDescription></DialogHeader>
          <div className="space-y-2">
            {dayVisits.map((v) => (
              <div key={v.id} className="border border-border rounded-sm p-3" data-testid={`visit-item-${v.number}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-sm">{v.party_name} <span className="font-mono text-xs text-muted-foreground">· {v.number}</span></p>
                    <p className="text-xs text-muted-foreground">{v.time || "Sin hora"} · {v.purpose || "Sin motivo"}</p>
                  </div>
                  <Badge className={`rounded-sm ${STATUS_COLORS[v.status] || ""}`}>{v.status}</Badge>
                </div>
                {v.result && <p className="text-xs mt-1"><span className="text-muted-foreground">Resultado:</span> {v.result}</p>}
                {(v.evidence || []).length > 0 && <p className="text-xs mt-1 text-primary flex items-center gap-1"><Paperclip className="h-3 w-3" />{v.evidence.length} evidencia(s)</p>}
                <div className="flex gap-2 mt-2">
                  {can("visits:edit") && <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs gap-1" onClick={() => openEdit(v)} data-testid={`edit-visit-${v.number}`}><Pencil className="h-3 w-3" /> Editar</Button>}
                  {can("visits:delete") && <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs text-destructive" onClick={() => setDelTarget(v)}><Trash2 className="h-3 w-3" /></Button>}
                </div>
              </div>
            ))}
          </div>
          {can("visits:create") && <Button className="rounded-sm w-full gap-2" onClick={() => openCreate(selectedDay)} data-testid="add-visit-this-day"><Plus className="h-4 w-4" /> Nueva visita este día</Button>}
        </DialogContent>
      </Dialog>

      {/* Create/edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="visit-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? `Editar visita ${editing.number}` : "Programar visita"}</DialogTitle>
            <DialogDescription>Cliente/prospecto, fecha, resultado y evidencia.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Visitar a</Label>
              <Select value={form.party_type} onValueChange={(v) => setForm({ ...form, party_type: v, client_id: "", prospect_id: "" })}>
                <SelectTrigger className="rounded-sm" data-testid="visit-party-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="client">Cliente</SelectItem><SelectItem value="prospect">Prospecto</SelectItem><SelectItem value="cold">Cliente frío</SelectItem></SelectContent></Select></div>
            {form.party_type === "client" ? (
              <div className="space-y-1.5 sm:col-span-3"><Label className="text-xs font-bold uppercase tracking-widest">Cliente *</Label>
                <SearchableSelect value={form.client_id} onChange={(v) => setForm({ ...form, client_id: v })} testid="visit-client"
                  placeholder="Selecciona un cliente" searchPlaceholder="Escribe para buscar cliente..."
                  options={clients.map((c) => ({ value: c.id, label: clientLabel(c) }))} /></div>
            ) : (
              <div className="space-y-1.5 sm:col-span-3"><Label className="text-xs font-bold uppercase tracking-widest">{form.party_type === "cold" ? "Cliente frío" : "Prospecto"} *</Label>
                <SearchableSelect value={form.prospect_id} onChange={(v) => setForm({ ...form, prospect_id: v })} testid="visit-prospect"
                  placeholder={form.party_type === "cold" ? "Selecciona un cliente frío" : "Selecciona un prospecto"} searchPlaceholder="Escribe para buscar..."
                  options={prospects.filter((p) => form.party_type === "cold" ? p.segment === "cold" : p.segment !== "cold").map((p) => ({ value: p.id, label: p.company }))} /></div>
            )}
            <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Fecha *</Label>
              <Input type="date" className="rounded-sm" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="visit-date" /></div>
            <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Hora</Label>
              <Input type="time" className="rounded-sm" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="visit-time" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs font-bold uppercase tracking-widest">Contacto</Label>
              <Input className="rounded-sm" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Persona a visitar" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs font-bold uppercase tracking-widest">Motivo</Label>
              <Input className="rounded-sm" value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="visit-purpose" placeholder="Ej. Presentar catálogo" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs font-bold uppercase tracking-widest">Lugar / dirección</Label>
              <Input className="rounded-sm" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Estado</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-sm" data-testid="visit-status"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(STATUS_COLORS).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Resultado</Label>
            <Textarea className="rounded-sm" value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })} data-testid="visit-result" placeholder="Qué ocurrió en la visita" /></div>
          <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Notas</Label>
            <Textarea className="rounded-sm" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest">Evidencia (fotos / documentos)</Label>
            <label className="flex items-center gap-2 mt-1.5 cursor-pointer border border-dashed border-border rounded-sm p-3 text-sm text-muted-foreground hover:bg-muted" data-testid="visit-upload-label">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Adjuntar archivo
              <input type="file" className="hidden" accept="image/*,application/pdf" onChange={upload} data-testid="visit-file-input" />
            </label>
            <div className="flex flex-wrap gap-2 mt-2">
              {form.evidence.map((f) => (
                <div key={f.id} className="relative border border-border rounded-sm overflow-hidden" data-testid={`visit-evidence-${f.id}`}>
                  {f.is_image ? <img src={fileUrl(f.id)} alt="ev" className="h-16 w-16 object-cover" /> : <div className="h-16 w-16 flex items-center justify-center bg-muted text-[10px] p-1 text-center">{f.original_filename}</div>}
                  <button onClick={() => rmEvidence(f.id)} className="absolute top-0 right-0 bg-destructive text-white p-0.5"><X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm font-bold" onClick={save} data-testid="save-visit-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar visita?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará <strong>{delTarget?.number}</strong> y su evento de agenda.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={doDelete} data-testid="confirm-delete-visit">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
